/*
 * Keeps the protection product from outnumbering the frames it protects.
 *
 * Frames can leave the cart in ways the customer does not connect to the protection they bought
 * for them: lowering a frame quantity, or removing a frame line altogether. This trims the
 * protection lines back down so they never exceed the frame quantity, and removes them entirely
 * once no frames are left.
 *
 * The correction runs inside the request that changed the cart (through theme.utils.reconcileCart),
 * before that request publishes its cartUpdate. The cart is therefore rendered once, with the final
 * state, at the same moment it would be rendered anyway. Publishing a second, delayed cartUpdate
 * instead would re-render the cart under the shopper (losing focus and anything half-typed into
 * another line) and re-trigger every other cartUpdate subscriber.
 *
 * Product IDs come from theme.frameProtection, rendered by the 'cart-frame-protection' snippet.
 */
(function () {
  const config = window.theme && theme.frameProtection;
  if (!config || !Array.isArray(config.frameProductIds) || !config.protectionProductId) return;

  const frameProductIds = config.frameProductIds.map(Number);
  const protectionProductId = Number(config.protectionProductId);

  function getQuantities(items) {
    return items.reduce(
      (totals, item) => {
        const productId = Number(item.product_id);

        if (frameProductIds.includes(productId)) {
          totals.frames += item.quantity;
        }
        else if (productId === protectionProductId) {
          totals.protection += item.quantity;
          totals.protectionLines.push(item);
        }

        return totals;
      },
      { frames: 0, protection: 0, protectionLines: [] }
    );
  }

  /*
   * Takes a full cart state (as returned by /cart.js, /cart/change.js or /cart/update.js) and
   * resolves to the state to publish. When protection exceeds the frames, the excess is removed
   * and the corrected state is returned, re-rendering the same sections the caller asked for.
   */
  async function reconcile(cart, options = {}) {
    if (!Array.isArray(cart.items)) return cart;

    const { frames, protection, protectionLines } = getQuantities(cart.items);
    if (protection <= frames) return cart;

    // Trim the most recently added protection lines first.
    const updates = {};
    let excess = protection - frames;

    for (let index = protectionLines.length - 1; index >= 0 && excess > 0; index--) {
      const line = protectionLines[index];
      const removed = Math.min(line.quantity, excess);

      updates[line.key] = line.quantity - removed;
      excess -= removed;
    }

    const sections = cart.sections ? Object.keys(cart.sections) : [];
    const body = JSON.stringify({ updates, sections, ...(options.sectionsUrl ? { sections_url: options.sectionsUrl } : {}) });
    const response = await fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } });
    const correctedState = await response.json();

    // A failed correction leaves the cart as the caller got it; the cart UI still caps the line.
    if (!response.ok || correctedState.errors || correctedState.status) return cart;

    return correctedState;
  }

  config.reconcile = reconcile;
})();
